import * as React from 'react';
import {
  ClientOnly,
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';

import { ThemeProvider } from '@/features/theme/components/theme-provider';
import { LayoutModeProvider } from '@/features/reader/hooks/use-layout-mode';
import { ReviewModeProvider } from '@/features/memorization/hooks/use-review-mode';

import appCss from '../styles.css?url';

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
}>()({
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1, viewport-fit=cover',
      },
      {
        title: 'Haamil - Quran Memorization',
      },
      {
        name: 'description',
        content: 'Haamil is a Quran memorization app using spaced repetition.',
      },
      {
        name: 'theme-color',
        content: '#1a1a1a',
      },
      {
        name: 'apple-mobile-web-app-capable',
        content: 'yes',
      },
      {
        name: 'apple-mobile-web-app-status-bar-style',
        content: 'black-translucent',
      },
      {
        name: 'apple-mobile-web-app-title',
        content: 'Haamil',
      },
    ],
    links: [
      {
        rel: 'stylesheet',
        href: appCss,
      },
      {
        rel: 'apple-touch-icon',
        href: '/icons/icon-192.png',
      },
      {
        rel: 'manifest',
        href: '/manifest.webmanifest',
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: () => <p>Page not found</p>,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <Outlet />
    </QueryClientProvider>
  );
}

function RootShell({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    void import('@/lib/init-sentry');
    if (import.meta.env.DEV) {
      void import('react-grab').then(() => {
        window.__REACT_GRAB__?.setToolbarState({ edge: 'right', ratio: 0.5 });
      });
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js', { scope: '/' })
        .catch((err) => console.warn('SW registration failed:', err));

      // Forward critical SW errors to Sentry
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SW_FETCH_ERROR') {
          void import('@/lib/init-sentry').then(({ Sentry }) => {
            Sentry.captureMessage('Service worker fetch failed', {
              level: 'warning',
              extra: {
                url: event.data.url,
                error: event.data.error,
                cacheName: event.data.cacheName,
              },
            });
          });
        }
      });
    }
  }, []);

  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        <ClientOnly
          fallback={
            <div id="root" className="bg-background">
              {children}
            </div>
          }
        >
          <ThemeProvider defaultTheme="system" storageKey="haamil-ui-theme">
            <LayoutModeProvider>
              <ReviewModeProvider>
                <div id="root" className="bg-background">
                  {children}
                </div>
              </ReviewModeProvider>
            </LayoutModeProvider>
          </ThemeProvider>
        </ClientOnly>
        <Scripts />
      </body>
    </html>
  );
}
